<template>
  <div class="bill-batch-export-page">
    <a-card :bordered="false" class="toolbar-card">
      <div class="toolbar-row">
        <div class="toolbar-left">
          <div class="page-title">批量导出账单</div>
          <div class="muted">按月单选 · 支持按楼层明细/全部催款单</div>
        </div>
        <div class="toolbar-right">
          <a-button @click="goBack">返回</a-button>
        </div>
      </div>
    </a-card>

    <a-card :bordered="false" class="content-card">
      <a-form layout="vertical">
        <a-form-item label="导出模式" required>
          <a-radio-group v-model="mode" button-style="solid">
            <a-radio-button value="floor_detail">按楼层导出账单明细</a-radio-button>
            <a-radio-button value="notice">生成全部催款单</a-radio-button>
          </a-radio-group>
        </a-form-item>

        <a-form-item label="月份（按月单选）" required>
          <a-month-picker v-model="month" format="YYYY-MM" style="width: 220px" />
        </a-form-item>

        <template v-if="mode === 'floor_detail'">
          <a-form-item label="楼号" required>
            <a-select
              v-model="buildingNo"
              placeholder="请选择楼号"
              style="width: 220px"
              :loading="buildingLoading"
              @change="onBuildingChange"
            >
              <a-select-option v-for="b in buildingOptions" :key="b" :value="b">{{ b }}</a-select-option>
            </a-select>
          </a-form-item>

          <a-form-item label="楼层（可多选，不选则导出全部楼层）">
            <a-select
              v-model="floors"
              mode="multiple"
              placeholder="请选择楼层"
              style="width: 360px"
              :loading="floorLoading"
              allowClear
            >
              <a-select-option v-for="f in floorOptions" :key="String(f)" :value="String(f)">{{ f }}层</a-select-option>
              <a-select-option v-if="hasOtherFloor" key="other" value="other">其他楼层</a-select-option>
            </a-select>
          </a-form-item>
        </template>

        <template v-else>
          <a-form-item label="本次包含费用类型" required>
            <a-checkbox-group v-model="includeTypes">
              <a-checkbox value="water">水费</a-checkbox>
              <a-checkbox value="electric">电费</a-checkbox>
              <a-checkbox value="property">物业费</a-checkbox>
            </a-checkbox-group>
          </a-form-item>
        </template>

        <div style="display:flex; gap: 8px; margin-top: 8px;">
          <a-button type="primary" :loading="exporting" @click="doExport">导出</a-button>
          <a-button :disabled="exporting" @click="reset">重置</a-button>
        </div>
      </a-form>
    </a-card>
  </div>
</template>

<script>
import request from "@/utils/request";
import moment from "moment";

export default {
  name: "BillBatchExport",
  data() {
    return {
      mode: "floor_detail",
      month: moment(),

      buildingOptions: [],
      buildingLoading: false,
      buildingNo: "",

      floorOptions: [],
      hasOtherFloor: false,
      floorLoading: false,
      floors: [],

      includeTypes: ["water", "electric", "property"],

      exporting: false,
    };
  },
  async mounted() {
    await this.loadBuildingOptions();
  },
  methods: {
    goBack() {
      if (this.$router) this.$router.back();
    },
    toast(msg, type = "info") {
      if (this.$message && this.$message[type]) this.$message[type](msg);
    },
    reset() {
      this.mode = "floor_detail";
      this.month = moment();
      this.buildingNo = "";
      this.floors = [];
      this.floorOptions = [];
      this.hasOtherFloor = false;
      this.includeTypes = ["water", "electric", "property"];
    },
    async loadBuildingOptions() {
      this.buildingLoading = true;
      try {
        const res = await request({ url: "/electricity/accounts/building-options/", method: "get" });
        const data = res?.data ?? res;
        this.buildingOptions = Array.isArray(data?.building_nos) ? data.building_nos : [];
      } catch (e) {
        console.error(e);
        this.buildingOptions = [];
        this.toast("加载楼号选项失败", "error");
      } finally {
        this.buildingLoading = false;
      }
    },
    async onBuildingChange() {
      this.floors = [];
      this.floorOptions = [];
      this.hasOtherFloor = false;
      if (!this.buildingNo) return;
      this.floorLoading = true;
      try {
        const res = await request({
          url: "/electricity/accounts/floor-options/",
          method: "get",
          params: { building_no: this.buildingNo },
        });
        const data = res?.data ?? res;
        this.floorOptions = Array.isArray(data?.floors) ? data.floors : [];
        this.hasOtherFloor = !!data?.has_other;
      } catch (e) {
        console.error(e);
        this.toast("加载楼层选项失败", "error");
      } finally {
        this.floorLoading = false;
      }
    },
    async doExport() {
      const monthStr = this.month ? this.month.format("YYYY-MM") : "";
      if (!monthStr) return this.toast("请选择月份", "warning");

      if (this.mode === "floor_detail") {
        if (!this.buildingNo) return this.toast("请选择楼号", "warning");
      } else {
        if (!this.includeTypes || this.includeTypes.length === 0) return this.toast("请至少选择一种费用类型", "warning");
      }

      this.exporting = true;
      try {
        const params = {
          month: monthStr,
          mode: this.mode,
        };
        if (this.mode === "floor_detail") {
          params.building_no = this.buildingNo;
          if (this.floors && this.floors.length) params.floors = this.floors.join(",");
        } else {
          params.include_types = (this.includeTypes || []).join(",");
        }

        const res = await request({
          url: "/electricity/batch-bills/export/",
          method: "get",
          params,
          responseType: "blob",
        });

        const blob = new Blob([res.data || res], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `batch_bills_${this.mode}_${monthStr}.xlsx`;
        document.body.appendChild(a);
        a.click();
        URL.revokeObjectURL(url);
        document.body.removeChild(a);
        this.toast("导出成功", "success");
      } catch (e) {
        console.error(e);
        this.toast("导出失败，请重试", "error");
      } finally {
        this.exporting = false;
      }
    },
  },
};
</script>

<style scoped>
.bill-batch-export-page {
  padding: 16px;
}
.toolbar-card,
.content-card {
  border-radius: 14px;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.06);
}
.toolbar-card {
  margin-bottom: 16px;
}
.toolbar-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
}
.toolbar-right {
  display: flex;
  gap: 8px;
}
.page-title {
  font-size: 18px;
  font-weight: 800;
  line-height: 1.2;
}
.muted {
  color: rgba(0, 0, 0, 0.45);
  margin-top: 6px;
}
</style>

